/**
 * Offline localized country names backed by the i18n-iso-countries dataset.
 *
 * Intl.DisplayNames is unreliable on Hermes, so we ship the country-name data
 * instead of relying on the runtime. Keys are ISO 3166-1 alpha-2 codes: the
 * same `code` exposed by country-data.ts (`CountryCode.code`) and the
 * `option.isoCode` field on STUDIO_COUNTRIES.
 */
import * as countries from 'i18n-iso-countries/index';
import enLocale from 'i18n-iso-countries/langs/en.json';
import arLocale from 'i18n-iso-countries/langs/ar.json';
import type { SupportedLocale } from '../hooks/useLocale';

let registered = false;

function ensureRegistered() {
  if (registered) return;
  countries.registerLocale(enLocale as countries.LocaleData);
  countries.registerLocale(arLocale as countries.LocaleData);
  registered = true;
}

/**
 * Returns the localized country name for an ISO 3166-1 alpha-2 code.
 *
 * - `locale === 'ar'` -> Arabic name (e.g. `AE` -> the Arabic UAE name),
 *   falling back to `fallback` (the existing English label) when the Arabic
 *   name is missing.
 * - any other locale -> `fallback` when provided, otherwise the English name.
 *
 * Final fallback is the raw ISO code so the UI never renders blank.
 */
export function localizedCountryName(
  isoCode: string,
  locale: SupportedLocale,
  fallback?: string,
): string {
  ensureRegistered();
  const code = isoCode?.toUpperCase();
  if (locale === 'ar') {
    const arName = code ? countries.getName(code, 'ar') : undefined;
    if (arName) return arName;
  }
  if (fallback) return fallback;
  const enName = code ? countries.getName(code, 'en') : undefined;
  return enName ?? isoCode;
}

/**
 * Normalizes text for locale-tolerant substring search.
 *
 * Latin text is only lowercased/trimmed. Arabic text is additionally folded so
 * real-world typing matches the canonical dataset spelling. Written with \u
 * escapes so no literal Arabic sits in source and no character range can
 * accidentally span the base-letter block (U+0621-U+064A):
 * - strips marks/harakat (U+0610-U+061A, U+064B-U+065F), superscript alef
 *   (U+0670) and tatweel (U+0640)
 * - unifies alef variants (U+0622/23/25/71 -> U+0627), alef-maksura / Farsi yeh
 *   (U+0649/U+06CC -> U+064A), taa marbuta (U+0629 -> U+0647) and hamza carriers
 *   (U+0624 -> U+0648, U+0626 -> U+064A), and drops bare hamza (U+0621)
 *
 * Because matching is substring-based, the leading article "al-" needs no
 * special handling: a bare-name query still matches the prefixed dataset name.
 */
export function normalizeSearchText(input: string): string {
  return (input ?? '')
    .trim()
    .toLowerCase()
    // strip Arabic harakat / combining marks (no base letters affected)
    .replace(/\p{Mn}/gu, '')
    .replace(/[آأإٱ]/g, 'ا')
    .replace(/[ىی]/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي')
    .replace(/ء/g, '');
}
